import { Button } from "@/components/Button";
import { Card, CardContent } from "@/components/Card";
import { UserAvatar } from "@/components/UserAvatar";
import { Award, Briefcase, Calendar, Gift, TrendingUp, MapPin, Cake } from "lucide-react";
import AppLayout from '@/layouts/AppLayout';
import { RedemptionHistory } from './Profile/RedemptionHistory';

export default function Profile({ profile, redemptions = [] }) {

  if (!profile) return <div>Cargando...</div>;

  const boost = profile.allowance_boost || 0;
  const allowance = profile.monthly_allowance || 0;
  const spent = profile.current_month_spent || 0;
  const stats = {
    balance: profile.points_balance || 0,
    allowance: allowance,
    boost: boost,
    spent: spent,
    totalAvailable: profile.current_month_budget || 0,
    received_total: profile.current_month_received || 0,
    given_total: profile.current_month_given || 0
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Profile Header */}
      <div className="relative mb-20">
        <div className="h-48 w-full bg-gradient-to-r from-green-600 to-emerald-500 rounded-b-[2.5rem] shadow-lg"></div>
        <div className="absolute top-24 left-0 w-full px-4 sm:px-8">
          <Card className="max-w-5xl mx-auto overflow-visible border-none shadow-xl bg-white/95 backdrop-blur-sm">
            <CardContent className="p-0">
              <div className="flex flex-col md:flex-row items-center md:items-end -mt-16 md:-mt-12 px-8 pb-8">
                <div className="relative">
                  <UserAvatar
                    user={profile}
                    size={144}
                    className="border-4 border-white shadow-2xl bg-white"
                  />
                  <div className="absolute bottom-4 right-4 bg-green-500 w-6 h-6 rounded-full border-4 border-white" title="Online now"></div>
                </div>

                <div className="flex-1 mt-6 md:mt-0 md:ml-8 text-center md:text-left space-y-2 pt-12 md:pt-0">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{profile.full_name}</h1>
                      <p className="text-gray-500 font-medium">{profile.job_title}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-center md:justify-start gap-1.5 pt-2 text-xs text-gray-600 overflow-x-auto">
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-50 border border-gray-100 whitespace-nowrap">
                      <Briefcase className="w-3 h-3 text-green-600" />
                      {profile.department?.name}
                    </span>
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-50 border border-gray-100 whitespace-nowrap">
                      <MapPin className="w-3 h-3 text-green-600" />
                      {profile.location || 'Remoto'}
                    </span>
                    {profile.date_of_birth && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-50 border border-gray-100 whitespace-nowrap">
                        <Cake className="w-3 h-3 text-green-600" />
                        {formatDate(profile.date_of_birth)}
                      </span>
                    )}
                    {profile.hired_on && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-50 border border-gray-100 whitespace-nowrap">
                        <Calendar className="w-3 h-3 text-green-600" />
                        Ingreso: {formatDate(profile.hired_on)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-12 space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: "Saldo actual", value: stats.balance, icon: WalletIcon, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
            { label: "Presupuesto mes", value: stats.totalAvailable, icon: Gift, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100", breakdown: stats.boost > 0 ? `${stats.allowance} base + ${stats.boost} boost` : null },
            { label: "Recibidos este mes", value: stats.received_total, icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-100" },
            { label: "Dados este mes", value: stats.given_total, icon: Award, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-100" },
          ].map((stat, i) => (
            <Card key={i} className={`group hover:shadow-lg transition-all duration-300 border ${stat.border}`}>
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">{stat.label}</p>
                  <p className={`text-3xl font-bold tracking-tight ${stat.color}`}>{stat.value}</p>
                  {stat.breakdown && (
                    <p className="text-xs font-medium text-gray-600 mt-1">
                      {stat.breakdown}
                    </p>
                  )}
                </div>
                <div className={`p-4 rounded-2xl ${stat.bg} ${stat.color} group-hover:scale-110 transition-transform duration-300`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Redemption History */}
        <RedemptionHistory redemptions={redemptions} />
      </div>
    </div>
  );
}

Profile.layout = page => <AppLayout children={page} />;

function WalletIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
      <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
      <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z" />
    </svg>
  )
}
